import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRightIcon,
  ArchiveBoxIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  GlobeAltIcon,
  GlobeAsiaAustraliaIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  TruckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import InteractiveWorldMap, { REGIONS, Region } from '../components/InteractiveWorldMap';
import { getCountryByCode } from '../data/countryData';
import { PageHero } from '../components/page-templates/SitePageTemplates';

const warehouseTheme = process.env.REACT_APP_LANDING === 'warehouse';

const CountryFlag: React.FC<{
  code: string;
  name: string;
  size?: 'sm' | 'md' | 'lg';
}> = ({ code, name, size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-5',
    md: 'h-5 w-6',
    lg: 'h-6 w-8',
  };

  return (
    <img
      src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
      alt={`${name} flag`}
      className={`${sizeClasses[size]} rounded-sm object-cover shadow-sm`}
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};

const heroStats = [
  { number: '140+', label: 'Countries served', icon: GlobeAsiaAustraliaIcon },
  { number: '240+', label: 'Partners', icon: MapPinIcon },
  { number: '6', label: 'Continents', icon: TruckIcon },
  { number: '24/7', label: 'Support', icon: BuildingOffice2Icon },
];

const services = [
  {
    title: 'Air Freight',
    description: 'Express and standard cargo delivered reliably worldwide.',
    icon: PaperAirplaneIcon,
  },
  {
    title: 'Ocean Freight',
    description: 'FCL and LCL sea freight with full visibility and competitive rates.',
    icon: ArchiveBoxIcon,
  },
  {
    title: 'Road Transport',
    description: 'Cross-border trucking and last-mile delivery, on time, every time.',
    icon: TruckIcon,
  },
  {
    title: 'Customs Brokerage',
    description: 'Expert clearance, regulatory compliance, and risk mitigation.',
    icon: ClipboardDocumentCheckIcon,
  },
];

const whyChooseBullets = [
  'Local expertise in 140+ countries',
  'Customs brokerage & compliance support',
  '24/7 shipment tracking & visibility',
  'Dedicated account management',
  'Competitive rates, no hidden fees',
  'End-to-end supply chain solutions',
];

const GlobalCoverage: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const allCountries = useMemo(
    () =>
      REGIONS.flatMap((region) =>
        region.countries.map((country) => ({
          ...country,
          region,
        }))
      ),
    []
  );

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];

    return allCountries
      .filter(
        (country) =>
          country.name.toLowerCase().includes(query) ||
          country.region.name.toLowerCase().includes(query)
      )
      .slice(0, 8);
  }, [allCountries, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountryClick = (countryCode: string) => {
    const country = getCountryByCode(countryCode);
    if (country) {
      navigate(`/countries/${country.slug}`);
    }
  };

  return (
    <div className="bg-white">
      {warehouseTheme ? <PageHero
        eyebrow="Global coverage"
        heroIcon={GlobeAltIcon}
        title="Local knowledge. Global reach."
        highlight="Global reach."
        description="Move technology and cargo across borders with one connected logistics partner. Explore the countries and regions supported by the TrueLog network."
        actions={[{ label: 'Get a quote', href: '/contact-us' }]}
        badges={['Freight', 'Customs', 'Warehousing']}
        metrics={heroStats.map((stat) => ({ value: stat.number, label: stat.label }))}
        panelEyebrow="Connected network"
        panelTitle="From Singapore to your next market"
        panelItems={services.slice(0, 3).map((service) => ({ icon: service.icon, title: service.title, description: service.description }))}
      /> : <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50 pb-16 pt-24 sm:pt-28">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gradient-to-br from-[#385CF2]/15 via-[#0E9ED5]/10 to-transparent blur-3xl" />
        </div>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mx-auto max-w-4xl text-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[#385CF2]/20 bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#385CF2]">
              <GlobeAltIcon className="h-4 w-4" />
              Global Coverage
            </span>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
              Reach 140+ countries worldwide, instantly.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-600">
              We provide frictionless, compliant logistics from Singapore to every corner of the globe. Click on any region on the map to explore our coverage and services.
            </p>
            <div className="mt-8 flex justify-center">
              <Link
                to="/contact-us"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#385CF2] to-[#0E9ED5] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#385CF2]/25 transition-transform hover:-translate-y-0.5"
              >
                Get a Quote
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>

          <div className="mx-auto mt-14 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {heroStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  className="rounded-[24px] border border-slate-200/80 bg-white p-6 text-center shadow-[0_20px_50px_-32px_rgba(15,23,42,0.22)]"
                >
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] text-white shadow-md">
                    <Icon className="h-6 w-6" />
                  </div>
                  <p className="mt-4 font-display text-3xl font-bold text-slate-950">{stat.number}</p>
                  <p className="mt-1 text-sm font-medium text-slate-500">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>}

      <section className="relative px-4 pb-6 pt-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-3xl text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Explore Our Global Network
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-600">
              Hover over a country to see how we serve it, or pick a region to list every country we cover.
            </p>
          </motion.div>

          <div className="mt-8 rounded-[34px] border border-white/80 bg-white/[0.92] p-6 shadow-[0_32px_100px_-46px_rgba(15,23,42,0.32)] backdrop-blur-2xl lg:p-8">
            <div ref={searchRef} className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                placeholder="Search countries or regions..."
                className="w-full rounded-2xl border border-slate-200/80 bg-slate-50 py-4 pl-12 pr-12 text-slate-900 outline-none transition-colors focus:border-cyan-300"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setIsSearchFocused(false);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              ) : null}

              <AnimatePresence>
                {isSearchFocused && searchResults.length > 0 ? (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_30px_80px_-44px_rgba(15,23,42,0.32)]"
                  >
                    {searchResults.map((result) => {
                      const countryData = getCountryByCode(result.code);

                      return (
                        <button
                          key={`${result.region.id}-${result.code}`}
                          type="button"
                          onClick={() => {
                            if (countryData) {
                              navigate(`/countries/${countryData.slug}`);
                            } else {
                              setSelectedRegion(result.region);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                            setSearchQuery('');
                            setIsSearchFocused(false);
                          }}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                        >
                          <CountryFlag code={result.code} name={result.name} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-slate-950">{result.name}</p>
                            <p className="text-xs text-slate-500">{result.region.name}</p>
                          </div>
                          {result.hasOffice ? (
                            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-600">
                              Office
                            </span>
                          ) : null}
                          {result.isPartner ? (
                            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-600">
                              Partner
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>

            {isSearchFocused && searchQuery.trim() && searchResults.length === 0 ? (
              <div className="mt-3 rounded-2xl border border-slate-200/80 bg-slate-50 px-4 py-4 text-sm text-slate-500">
                No countries found for "{searchQuery}".
              </div>
            ) : null}

            <div className="mt-6 rounded-[30px] border border-slate-200/80 bg-slate-50 p-4">
              <InteractiveWorldMap
                selectedRegion={selectedRegion}
                onRegionSelect={setSelectedRegion}
                onCountryClick={handleCountryClick}
                showOfficeMarkers={true}
              />
            </div>

          </div>
        </div>
      </section>

      <AnimatePresence>
        {selectedRegion ? (
          <motion.section
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden bg-slate-950 py-10 text-white"
          >
            <div className="container-custom">
              <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">Selected region</p>
                      <h2 className="mt-2 text-4xl font-bold tracking-tight text-white">{selectedRegion.name}</h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedRegion(null)}
                      className="rounded-2xl border border-white/10 bg-white/[0.06] p-2.5 transition-colors hover:bg-white/[0.12]"
                      aria-label="Close region panel"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="mt-4 text-sm leading-8 text-slate-300">{selectedRegion.description}</p>

                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="rounded-[24px] border border-white/10 bg-white/[0.06] p-5">
                      <p className="font-display text-3xl font-bold text-white">{selectedRegion.stats.markets}</p>
                      <p className="mt-2 text-sm text-slate-300">Countries</p>
                    </div>
                    <div className="rounded-[24px] border border-white/10 bg-white/[0.06] p-5">
                      <p className="font-display text-3xl font-bold text-white">{selectedRegion.stats.partners}</p>
                      <p className="mt-2 text-sm text-slate-300">Partners</p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Countries covered</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {selectedRegion.countries.map((country) => {
                      const countryData = getCountryByCode(country.code);

                      return countryData ? (
                        <Link
                          key={country.name}
                          to={`/countries/${countryData.slug}`}
                          className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 transition-colors hover:bg-white/[0.1]"
                        >
                          <CountryFlag code={country.code} name={country.name} />
                          <span className="text-sm text-slate-200">{country.name}</span>
                        </Link>
                      ) : (
                        <div
                          key={country.name}
                          className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3"
                        >
                          <CountryFlag code={country.code} name={country.name} />
                          <span className="text-sm text-slate-200">{country.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.section>
        ) : null}
      </AnimatePresence>

      <section className="bg-white py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-3xl text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              Coverage by Region
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-600">
              Explore our strategic presence across all continents with local expertise and customs knowledge.
            </p>
          </motion.div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {REGIONS.map((region, index) => (
              <motion.div
                key={region.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.05 }}
                onClick={() => {
                  setSelectedRegion(region);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="cursor-pointer overflow-hidden rounded-[30px] border border-slate-200/80 bg-white shadow-[0_24px_60px_-40px_rgba(15,23,42,0.28)] transition-transform hover:-translate-y-1"
              >
                <div
                  className="p-6 text-white"
                  style={{ background: 'linear-gradient(135deg, #385CF2, #0E9ED5)' }}
                >
                  <h3 className="text-2xl font-semibold text-white">{region.name}</h3>
                  <div className="mt-2 flex flex-wrap gap-4 text-sm text-white/90">
                    <span>{region.stats.markets} countries</span>
                    <span>{region.stats.partners} partners</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm leading-7 text-slate-600">{region.description}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {region.countries.slice(0, 5).map((country) => (
                      <span key={country.name} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs text-slate-600">
                        <CountryFlag code={country.code} name={country.name} size="sm" />
                        {country.name}
                      </span>
                    ))}
                    {region.countries.length > 5 ? (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1.5 text-xs text-slate-500">
                        +{region.countries.length - 5} more
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#385CF2]">
                    Explore region
                    <ArrowRightIcon className="h-4 w-4" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-3xl text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
              End-to-End ICT Logistics solutions available across all our markets
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-600">
              We deliver frictionless ICT logistics at scale, combining speed, compliance, and operational certainty in every region.
            </p>
          </motion.div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.05 }}
                  className="rounded-[28px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.22)]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] text-white shadow-md">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-slate-950">{service.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{service.description}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#385CF2] to-[#0E9ED5] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#385CF2]/25 transition-transform hover:-translate-y-0.5"
            >
              Explore How We Work
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
                Global scale, local expertise
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                We combine strategic locations across 6 continents with local knowledge and seamless customs support to make global ICT logistics frictionless.
              </p>
              <ul className="mt-8 space-y-4">
                {whyChooseBullets.map((bullet, index) => (
                  <motion.li
                    key={bullet}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-[#385CF2]" />
                    <span className="text-base text-slate-700">{bullet}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:sticky lg:top-24 lg:self-start"
            >
              <div
                className="rounded-[32px] p-8 text-white shadow-[0_30px_80px_-40px_rgba(56,92,242,0.55)]"
                style={{ background: 'linear-gradient(135deg, #385CF2, #0E9ED5)' }}
              >
                <h3 className="text-2xl font-bold text-white">Ready to Go Global?</h3>
                <p className="mt-4 text-base leading-7 text-white/90">
                  Get a customised logistics solution for your international shipping needs. Our experts are ready to help you navigate global trade.
                </p>
                <div className="mt-8 flex flex-col gap-3">
                  <Link
                    to="/contact-us"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-[#385CF2] transition-transform hover:-translate-y-0.5"
                  >
                    Get a Customised Solution
                    <ArrowRightIcon className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/contact-us"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/70 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                  >
                    Contact Sales Team
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-[36px] p-10 text-center text-white sm:p-14"
            style={{ background: 'linear-gradient(135deg, #385CF2, #0E9ED5)' }}
          >
            <div className="pointer-events-none absolute inset-0 opacity-30">
              <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
              <div className="absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
            </div>
            <div className="relative">
              <h2 className="mx-auto max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to simplify your global hardware logistics?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-white/90">
                Tell us the countries involved and how the programme is structured. We can help decide where office support, partner support, warehousing, or compliance layers need to sit.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/contact-us"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[#385CF2] transition-transform hover:-translate-y-0.5"
                >
                  Talk to experts
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 rounded-full border border-white/70 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                >
                  Explore Services
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GlobalCoverage;
